import type { TFunction } from 'i18next';

import { toApiError, type ApiError, type GatewayErrorCode } from '@/shared/api/errors';

/**
 * UI-facing classification of gateway errors.
 * Aligns with admin envelope `error.code` values (+ NETWORK / UNKNOWN).
 *
 * Note: use {@link UiErrorKind}'s `UNAUTHORIZED` for auth failures.
 * Do not rely on `TOKEN_EXPIRED` — JWT expiry surfaces as UNAUTHORIZED.
 */
export type UiErrorKind =
  | 'VALIDATION_ERROR'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'RATE_LIMITED'
  | 'SERVICE_UNAVAILABLE'
  | 'GATEWAY_TIMEOUT'
  | 'INTERNAL_ERROR'
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

export interface UiError {
  kind: UiErrorKind;
  /** Prefer this for ops-facing toasts and inline alerts. */
  message: string;
  /** Field/path issues when `details` is structured. */
  fieldErrors?: Record<string, string[]>;
  /** Multi-line validation / business details (when provided). */
  details?: string[];
  requestId?: string;
  status: number;
  code: string;
}

const KIND_FROM_CODE: Record<GatewayErrorCode, UiErrorKind> = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  GATEWAY_TIMEOUT: 'GATEWAY_TIMEOUT',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  UNKNOWN: 'UNKNOWN',
};

function getRequestIdFromApiError(err: ApiError): string | undefined {
  if (err.requestId) return err.requestId;
  const raw = err.raw as { requestId?: unknown } | undefined;
  return typeof raw?.requestId === 'string' && raw.requestId.length > 0 ? raw.requestId : undefined;
}

/** Support log — never log tokens; requestId is safe to keep for correlation. */
export function logApiError(err: unknown, context?: string): void {
  const apiErr = toApiError(err);
  const requestId = getRequestIdFromApiError(apiErr);
  console.warn('[api-error]', {
    context: context ?? undefined,
    code: apiErr.code,
    status: apiErr.status,
    message: apiErr.message,
    requestId: requestId ?? null,
    details: apiErr.details?.length ? apiErr.details : undefined,
    fieldErrors: apiErr.fieldErrors,
  });
}

export function toUiError(err: unknown): UiError {
  const apiErr = toApiError(err);
  const code = apiErr.code as GatewayErrorCode;
  const kind: UiErrorKind = KIND_FROM_CODE[code] ?? 'UNKNOWN';
  const requestId = getRequestIdFromApiError(apiErr);

  // Prefer server message for ops-facing text; kinds provide fallbacks later.
  const message = apiErr.message?.trim() || '';

  return {
    kind,
    message,
    fieldErrors: apiErr.fieldErrors,
    details: apiErr.details,
    requestId,
    status: apiErr.status,
    code: apiErr.code,
  };
}

function kindFallbackMessage(t: TFunction, kind: UiErrorKind): string {
  const keys: Record<UiErrorKind, string> = {
    UNAUTHORIZED: t('errors.kind.unauthorized', { ns: 'common' }),
    FORBIDDEN: t('errors.kind.forbidden', { ns: 'common' }),
    NOT_FOUND: t('errors.kind.notFound', { ns: 'common' }),
    CONFLICT: t('errors.kind.conflict', { ns: 'common' }),
    RATE_LIMITED: t('errors.kind.rateLimited', { ns: 'common' }),
    SERVICE_UNAVAILABLE: t('errors.kind.serviceUnavailable', { ns: 'common' }),
    GATEWAY_TIMEOUT: t('errors.kind.gatewayTimeout', { ns: 'common' }),
    INTERNAL_ERROR: t('errors.kind.internalError', { ns: 'common' }),
    VALIDATION_ERROR: t('errors.kind.validationError', { ns: 'common' }),
    NETWORK_ERROR: t('errors.kind.networkError', { ns: 'common' }),
    UNKNOWN: t('errors.kind.unknown', { ns: 'common' }),
  };
  return keys[kind];
}

function buildOpsDescription(ui: UiError, fallback: string): string {
  const message = ui.message.trim();
  const detailLines = ui.details?.filter(Boolean) ?? [];

  if (ui.kind === 'VALIDATION_ERROR' && detailLines.length > 0) {
    // Prefer multi-field detail list when present; keep top-level message if distinct.
    const joined = detailLines.join(' · ');
    if (!message || message === fallback) return joined;
    const alreadyCovered = detailLines.some(
      (d) => d === message || d.endsWith(`: ${message}`) || message.includes(d),
    );
    return alreadyCovered ? joined : `${message} · ${joined}`;
  }

  if (message) return message;
  return fallback;
}

/**
 * Ops-facing toast copy for gateway failures.
 *
 * - Prefers `error.message` from the API
 * - Surfaces `error.details[]` for multi-field validation
 * - Appends `requestId` for support correlation on severe/infra failures
 * - Logs full code/status/requestId via {@link logApiError}
 */
export function getUiErrorToastText(
  t: TFunction,
  err: unknown,
): { title: string; description?: string } {
  logApiError(err);

  const ui = toUiError(err);
  const title = t('errors.toast.title', { ns: 'common' });
  const fallback = kindFallbackMessage(t, ui.kind);
  let description = buildOpsDescription(ui, fallback);

  const appendRequestId =
    Boolean(ui.requestId) &&
    (ui.kind === 'INTERNAL_ERROR' ||
      ui.kind === 'SERVICE_UNAVAILABLE' ||
      ui.kind === 'GATEWAY_TIMEOUT' ||
      ui.kind === 'UNKNOWN');

  if (appendRequestId && ui.requestId) {
    description = t('errors.withRequestId', {
      ns: 'common',
      message: description,
      requestId: ui.requestId,
    });
  }

  return { title, description };
}

/**
 * Bind gateway `error.details` field paths onto a React Hook Form instance.
 * Returns true when at least one field error was applied.
 */
export function applyApiFieldErrors(
  fieldErrors: Record<string, string[]> | undefined,
  setError: (name: string, error: { type: string; message: string }) => void,
): boolean {
  if (!fieldErrors) return false;
  let applied = false;
  for (const [field, messages] of Object.entries(fieldErrors)) {
    const message = messages.find((m) => m.trim().length > 0)?.trim();
    if (!message) continue;
    setError(field, { type: 'server', message });
    applied = true;
  }
  return applied;
}

/**
 * Inline form / dialog alert line — server message preferred, kind fallback next.
 * Always logs `requestId` for support (not always shown in UI).
 */
export function getUiErrorMessage(t: TFunction, err: unknown): string {
  logApiError(err);
  const ui = toUiError(err);
  const fallback = kindFallbackMessage(t, ui.kind);
  return buildOpsDescription(ui, fallback);
}
